import type { Catalogue } from './core.js';

/**
 * Danish — the source catalogue.
 *
 * **This file defines the keys.** `MessageKey` is derived from it, and every
 * other locale is declared `Catalogue<MessageKey>`, so a translation that has
 * not been written does not compile. Adding a key here and nowhere else breaks
 * the build until English catches up, which is the point.
 *
 * Keys are namespaced by where they appear, not by what they say: `nav.*`,
 * `account.*`, `bank.*`. Two screens that happen to share a word still get two
 * keys — they will not stay the same word in every language, and a shared key
 * is how "Konto" ends up meaning both "account" and "ledger account" in the
 * same translation.
 *
 * Danish spelling and the domain vocabulary are the books': bogføring, bilag,
 * moms, kladde. Those are the words on the paperwork.
 */
export const da = {
  // ---------------------------------------------------------------------------
  // Shell
  // ---------------------------------------------------------------------------
  'app.name': 'Regnskab',
  'shell.menu': 'Menu',
  'shell.mainNav': 'Hovedmenu',
  'shell.account': 'Konto',
  'shell.accountMenu': 'Din konto',
  'shell.you': 'Dig',
  'shell.signOut': 'Log ud',
  'shell.signOutFailed': 'Du er ikke logget ud',

  'nav.overview': 'Overblik',
  'nav.bookkeeping': 'Bogføring',
  'nav.attachments': 'Bilag',
  'nav.inbound': 'Indgående post',
  'nav.bank': 'Bank',
  'nav.invoices': 'Fakturaer',
  'nav.contacts': 'Kontakter',
  'nav.vat': 'Moms',
  'nav.reports': 'Rapporter',
  'nav.settings': 'Indstillinger',
  'nav.chartOfAccounts': 'Kontoplan',
  'nav.saft': 'SAF-T',
  'nav.audit': 'Revisionsspor',
  'nav.mobileApp': 'Mobil-app',
  'nav.companyDetails': '{name} detaljer',
  'nav.companyDetailsFallback': 'Firmaets detaljer',
  'nav.myAccount': 'Min konto',
  'nav.mcp': 'MCP',

  // ---------------------------------------------------------------------------
  // Company switcher
  // ---------------------------------------------------------------------------
  'company.switch': 'Skift firma',
  'company.choose': 'Vælg firma',
  'company.companies': 'Firmaer',
  'company.active': 'aktiv',
  'company.switchFailed': 'Kunne ikke skifte firma',
  'company.add': 'Tilføj virksomhed',
  'company.add.title': 'Tilføj virksomhed',
  'company.add.name': 'Navn',
  'company.add.cvr': 'CVR-nummer',
  'company.add.cvrPlaceholder': '8 cifre',
  'company.add.cvrHelp':
    'Kan udfyldes senere. Kræves af SAF-T-eksporten og af en elektronisk faktura.',
  'company.add.nameRequired': 'Firmaet skal have et navn.',
  'company.add.cvrInvalid': 'Et CVR-nummer er præcis 8 cifre.',
  'company.add.warningTitle': 'Firmaet kan ikke slettes bagefter',
  'company.add.warning':
    'Revisionssporet kan ikke slettes, og det peger på firmaet. Så snart der er skrevet noget i regnskabet, bliver firmaet stående — også hvis det var en tastefejl. Tjek navnet.',
  'company.add.created': 'Firmaet er oprettet',
  'company.add.createdDetail': '{name} — du er ejer.',
  'company.add.failed': 'Firmaet blev ikke oprettet',

  // ---------------------------------------------------------------------------
  // Shared vocabulary
  // ---------------------------------------------------------------------------
  'common.save': 'Gem',
  'common.cancel': 'Annullér',
  'common.create': 'Opret',
  'common.tryAgain': 'Prøv igen',
  'common.copy': 'Kopiér',
  'common.copied': 'Kopieret',
  'common.hide': 'Skjul',
  'common.never': 'aldrig',
  'common.required': 'Feltet skal udfyldes.',

  // ---------------------------------------------------------------------------
  // Errors and auth boundary
  // ---------------------------------------------------------------------------
  'error.generic': 'Der opstod en fejl',
  'error.unreachable': 'Kunne ikke få forbindelse til serveren. Kontrollér at API’en kører.',
  'error.reference': 'Reference:',
  'auth.sessionFailed': 'Kunne ikke indlæse din session',
  'auth.noCompanies': 'Ingen firmaer',
  'auth.noCompaniesDetail':
    'Din bruger er ikke tilknyttet noget firma, så der er ingen bøger at vise. Bed en ejer om at tilføje dig.',

  // ---------------------------------------------------------------------------
  // Login
  // ---------------------------------------------------------------------------
  'login.email': 'E-mail',
  'login.password': 'Adgangskode',
  'login.submit': 'Log ind',
  'login.emailInvalid': 'Skriv en gyldig e-mailadresse.',
  'login.passwordMissing': 'Adgangskoden mangler.',
  'login.totpPrompt': 'Indtast koden fra din autentificeringsapp.',
  'login.totpLabel': 'Engangskode',
  'login.totpSubmit': 'Bekræft',
  'login.sessionNotKept':
    'Din e-mail og adgangskode blev godkendt, men browseren beholdt ikke sessionen. Det sker, når siden og API’en ligger på hver sit domæne — åbn dem på samme adresse.',

  // ---------------------------------------------------------------------------
  // Not found
  // ---------------------------------------------------------------------------
  'notFound.title': 'Siden findes ikke',
  'notFound.detail': 'Adressen svarer ikke til noget i systemet.',
  'notFound.back': 'Tilbage til overblik',

  // ---------------------------------------------------------------------------
  // Dashboard
  // ---------------------------------------------------------------------------
  'dash.title': 'Overblik',
  'dash.loadFailed': 'Overblikket kunne ikke hentes',
  'dash.clearTitle': 'Der er ikke noget udestående',
  'dash.clear':
    'Alle bilag er bogført, og alle banklinjer er gjort op. En åben momsperiode tæller ikke med her — den er normal midt i et kvartal.',
  'dash.outstandingTitle': 'Der er noget udestående',
  'dash.outstanding':
    'Tallene nedenfor er talt i databasen, ikke på en side af resultater, så de er de faktiske antal.',
  'dash.scope':
    'Overblikket viser bilag, kladde, bank og moms. Fakturaer og rapporter har deres egne sider — det er ikke opgjort her, hvad der mangler at blive faktureret.',

  'dash.unlinked.label': 'Bilag uden bogføring',
  'dash.unlinked.explanation':
    'Dokumenter der er lagt op, men endnu ikke knyttet til en postering. Et bilag der ikke er bogført, dokumenterer ingenting.',
  'dash.unlinked.action': 'Gå til bilag',
  'dash.undocumented.label': 'Posteringer uden bilag',
  'dash.undocumented.explanation':
    'Posteringer der ikke har et bilag knyttet til sig. Bogføringsloven kræver, at hver registrering kan dokumenteres, så det er dem her en revisor spørger til.',
  'dash.undocumented.action': 'Gå til kladde',
  'dash.drafts.label': 'Kladdeposteringer',
  'dash.drafts.explanation':
    'Posteringer der er skrevet, men ikke bogført endnu. De tæller ikke med i noget regnskab, ingen balance, ingen momsopgørelse og ingen SAF-T — så det er kun her, de bliver husket.',
  'dash.drafts.action': 'Se kladdeposteringer',
  'dash.unmatched.label': 'Banklinjer uden postering',
  'dash.unmatched.explanation':
    'Linjer fra kontoudtoget som der intet er bogført på. Hver af dem er en indbetaling eller udbetaling, bogføringen endnu ikke kender.',
  'dash.unmatched.action': 'Gå til bank',
  'dash.partial.label': 'Banklinjer der ikke går op',
  'dash.partial.explanation':
    'Matchet, men posteringerne summer ikke til linjens beløb. Forskellen står stadig uforklaret.',
  'dash.partial.action': 'Se forskellen',

  'dash.vat.title': 'Momsperioder',
  'dash.vat.none': 'Ingen åben periode. Åbn en, før perioden skal angives.',
  'dash.vat.first': 'Først i rækken',
  'dash.vat.notATask':
    'En åben periode er ikke en opgave i sig selv. Tallene er først endelige, når perioden angives og lukkes.',
  'dash.vat.action': 'Gå til moms',

  // ---------------------------------------------------------------------------
  // Journal — the ledger
  // ---------------------------------------------------------------------------
  'journal.title': 'Bogføring',
  'journal.newEntry': 'Ny postering',
  'journal.hideForm': 'Skjul kladde',
  'journal.searchReference': 'Søg på bilagsnummer',
  'journal.empty': 'Der er ikke bogført noget endnu.',
  'journal.noMatch': 'Ingen posteringer med bilagsnummer "{reference}".',
  'journal.col.account': 'Konto',
  'journal.col.text': 'Tekst',
  'journal.col.debit': 'Debet',
  'journal.col.credit': 'Kredit',
  'journal.col.vat': 'Moms',

  // ---------------------------------------------------------------------------
  // SAF-T
  // ---------------------------------------------------------------------------
  'saft.title': 'SAF-T',
  'saft.whatTitle': 'Hvad filen er',
  'saft.what':
    'SAF-T v2.1 er den fil, Skattestyrelsen kan bede om. Den dannes ud fra bogføringen for den valgte periode og valideres i CI mod Erhvervsstyrelsens eget skema.',
  'saft.choosePeriod': 'Vælg periode',
  'saft.bothInclusive': 'Begge datoer er inklusive.',
  'saft.generate': 'Dan SAF-T-fil',
  'saft.download': 'Hent {filename}',
  'saft.datesRequired': 'Både fra- og til-dato skal udfyldes.',
  'saft.generated': 'SAF-T-filen er dannet',
  'saft.generatedDetail': 'Hent den nedenfor.',
  'saft.failed': 'Filen blev ikke dannet',
  'saft.requiresTitle': 'Hvad filen kræver',
  'saft.requires.company':
    'Firmaet skal have CVR-nummer, adresse og bankkonto — skemaet kræver alle tre i filens hoved, og en pladsholder ville give en fil, der validerer og angiver den forkerte afsender.',
  'saft.requires.vatCodes':
    'Momskoder må højst fylde ni tegn. Ni af de officielle koder er længere, og eksporten afvises frem for at forkorte en kode, der er bogført.',
  'saft.requires.contacts':
    'En kontakt uden adresse udelades og nævnes som en advarsel — den blokerer ikke filen.',
  'saft.doneTitle': 'Filen er dannet',
  'saft.doneDetail':
    '{filename} · {characters} tegn. Filen er ikke gemt på serveren — den dannes ud fra bogføringen, hver gang der bedes om den.',

  // ---------------------------------------------------------------------------
  // Chart of accounts
  // ---------------------------------------------------------------------------
  'accounts.title': 'Kontoplan',
  'accounts.new': 'Ny konto',
  'accounts.search': 'Søg',
  'accounts.searchPlaceholder': 'Nummer eller navn',
  'accounts.type': 'Type',
  'accounts.allTypes': 'Alle',
  'accounts.showInactive': 'Vis inaktive',
  'accounts.none': 'Ingen konti matcher.',
  'accounts.col.number': 'Nummer',
  'accounts.col.name': 'Navn',
  'accounts.col.vat': 'Moms',
  'accounts.col.standard': 'Standard',
  'accounts.inactive': 'inaktiv',
  'accounts.edit': 'Rediger',

  'accounts.type.asset': 'Aktiv',
  'accounts.type.liability': 'Passiv',
  'accounts.type.equity': 'Egenkapital',
  'accounts.type.revenue': 'Indtægt',
  'accounts.type.expense': 'Udgift',

  'accounts.create.title': 'Ny konto',
  'accounts.create.beforeTitle': 'Før du opretter en',
  'accounts.create.before':
    'Kontoplanen er Erhvervsstyrelsens standardkontoplan, som er lovpligtig fra januar 2026. Rapporterne grupperer efter den, så en konto, du selv opretter, tæller kun med, hvis nummeret ligger i et interval, standardkontoplanen allerede summerer.',
  'accounts.create.bankHint':
    'Skal du bruge en bankkonto mere, er 6481–6489 ledige og lægger sig ind under 6490 Likvide beholdninger af sig selv.',
  'accounts.create.number': 'Kontonummer',
  'accounts.create.namePlaceholder': 'Bankkonto – Coffee Shop',
  'accounts.create.vatCode': 'Momskode',
  'accounts.create.vatCodeHelp': 'Valgfri. Sættes normalt kun på indtægts- og udgiftskonti.',
  'accounts.create.numberRequired': 'Kontonummeret skal udfyldes.',
  'accounts.create.numberDigits': 'Et kontonummer er kun cifre.',
  'accounts.create.nameRequired': 'Navnet skal udfyldes.',
  'accounts.create.created': 'Kontoen er oprettet',
  'accounts.create.failed': 'Kontoen blev ikke oprettet',

  'accounts.edit.title': 'Konto {code}',
  'accounts.edit.vatCodeHelp': 'Tom hvis kontoen ikke er momsbelagt',
  'accounts.edit.active': 'Aktiv',
  'accounts.edit.activeHelp':
    'En inaktiv konto kan ikke vælges ved bogføring, men beholder sin historik',
  'accounts.edit.saved': 'Gemt',
  'accounts.edit.failed': 'Kunne ikke gemme',

  // ---------------------------------------------------------------------------
  // Company settings
  // ---------------------------------------------------------------------------
  'settings.title': 'Indstillinger',
  'settings.loadFailed': 'Firmaet kunne ikke hentes',
  'settings.ownerOnlyTitle': 'Kun en ejer kan ændre firmaet',
  'settings.ownerOnly': 'Du kan se oplysningerne, men ikke gemme dem. Det kræver rollen ejer.',
  'settings.companySection': 'Firmaet',
  'settings.name': 'Navn',
  'settings.cvr': 'CVR-nummer',
  'settings.cvrPlaceholder': '8 cifre',
  'settings.cvrHelp': 'Kræves af SAF-T-eksporten og af en elektronisk faktura. Ryddes ved at lade feltet stå tomt.',
  'settings.address': 'Adresse',
  'settings.zip': 'Postnummer',
  'settings.city': 'By',
  'settings.fiscalSection': 'Regnskabsår og moms',
  'settings.fiscalStart': 'Regnskabsåret begynder',
  'settings.fiscalStartHelp': 'Skrives MM-DD — et regnskabsår har ingen årstal. 01-01 for kalenderår, 07-01 for et år der begynder 1. juli.',
  'settings.fiscalUnusedTitle': 'Regnskabsåret bruges ikke endnu',
  'settings.fiscalUnused': 'Rapporterne på /rapporter tager en periode, du selv vælger, og SAF-T-eksporten åbner på kalenderåret. Feltet her registrerer firmaets rigtige regnskabsår og ændrer ingen rapport.',
  'settings.vatPeriod': 'Momsperiode',
  'settings.vatPeriodHelp': 'Momsperioden siger, hvilken cyklus firmaet angiver på. Den opretter ikke perioder — de åbnes med deres egne datoer på Moms — og den flytter ikke perioder, der allerede findes. Et lukket kvartal bliver ved med at være et lukket kvartal.',
  'settings.vatPeriod.monthly': 'Måned',
  'settings.vatPeriod.quarterly': 'Kvartal',
  'settings.vatPeriod.biannual': 'Halvår',
  'settings.immutableSection': 'Kan ikke ændres',
  'settings.currency': 'Valuta',
  'settings.country': 'Land',
  'settings.currencyHelp': 'Alle beløb i bogføringen står allerede i firmaets valuta. At ændre feltet ville ikke omregne en eneste postering — det ville lave om på, hvad de alle sammen betyder. Et firma, der skal føres i en anden valuta, skal have sit eget regnskab.',
  'settings.saved': 'Indstillingerne er gemt',
  'settings.saveFailed': 'Indstillingerne blev ikke gemt',
  'settings.nameRequired': 'Navnet skal udfyldes.',
  'settings.cvrInvalid': 'Et CVR-nummer er præcis 8 cifre.',
  'settings.fiscalStartInvalid': 'Skrives MM-DD, for eksempel 01-01. Den 29. februar kan ikke bruges.',

  // ---------------------------------------------------------------------------
  // Invoices — list
  // ---------------------------------------------------------------------------
  'invoices.title': 'Fakturaer',
  'invoices.newDraft': 'Ny kladde',
  'invoices.filterLabel': 'Filtrér efter status',
  'invoices.filter.all': 'Alle',
  'invoices.filter.drafts': 'Kladder',
  'invoices.filter.sent': 'Sendte',
  'invoices.filter.paid': 'Betalte',
  'invoices.none': 'Ingen fakturaer her endnu.',
  'invoices.col.number': 'Nummer',
  'invoices.col.date': 'Dato',
  'invoices.col.customer': 'Kunde',
  'invoices.col.status': 'Status',
  'invoices.col.total': 'I alt',
  'invoices.noNumberYet': 'Intet nummer endnu',
  'invoices.open': 'Åbn',
  'invoices.numbersTitle': 'Numre tildeles ved udstedelse',
  'invoices.numbers': 'En kladde har intet nummer. Nummeret tildeles først når fakturaen udstedes, så en slettet kladde ikke efterlader et hul i en nummerrække, der efter loven skal være ubrudt.',
  'invoices.status.draft': 'Kladde',
  'invoices.status.sent': 'Sendt',
  'invoices.status.received': 'Modtaget',
  'invoices.status.paid': 'Betalt',
  'invoices.status.overdue': 'Forfalden',
  'invoices.status.cancelled': 'Annulleret',
  'period.from': 'Fra begyndelsen',
  'period.to': 'Til i dag',
  'period.fromLabel': 'Fra',
  'period.toLabel': 'Til',

  // ---------------------------------------------------------------------------
  // Contacts
  // ---------------------------------------------------------------------------
  'contacts.title': 'Kontakter',
  'contacts.new': 'Ny kontakt',
  'contacts.showDisabled': 'Vis deaktiverede',
  'contacts.none': 'Ingen kontakter endnu.',
  'contacts.col.name': 'Navn',
  'contacts.col.type': 'Type',
  'contacts.col.address': 'Adresse',
  'contacts.disabled': 'deaktiveret',
  'contacts.type.customer': 'Kunde',
  'contacts.type.supplier': 'Leverandør',
  'contacts.type.both': 'Begge',
  'contacts.create.title': 'Ny kontakt',
  'contacts.create.created': 'Kontakt oprettet',
  'contacts.create.failed': 'Kontakten blev ikke oprettet',
  'contacts.edit.title': 'Rediger {name}',
  'contacts.edit.saved': 'Gemt',
  'contacts.edit.failed': 'Kontakten blev ikke gemt',
  'contacts.active': 'Aktiv',
  'contacts.activeHelp': 'En deaktiveret kontakt kan ikke vælges på nye fakturaer, men beholder sine gamle. Kontakter slettes aldrig — udstedte fakturaer henviser til dem.',
  'contacts.cvr': 'CVR',
  'contacts.cvrPlaceholder': '8 cifre',
  'contacts.ean': 'EAN',
  'contacts.eanPlaceholder': '13 cifre',
  'contacts.email': 'E-mail',
  'contacts.address': 'Adresse',
  'contacts.zip': 'Postnummer',
  'contacts.city': 'By',
  'contacts.addressRequiredTitle': 'Adressen er ikke valgfri ved e-fakturering',
  'contacts.addressRequired': 'En elektronisk faktura navngiver begge parter fuldt ud. Uden adresse, by og postnummer afvises afsendelsen, og kontakten kan kun faktureres på papir.',
  'contacts.nameRequired': 'Kontakten skal have et navn.',
  'contacts.cvrInvalid': 'Et CVR-nummer er 8 cifre.',
  'contacts.eanInvalid': 'Et EAN-nummer er 13 cifre.',
  'contacts.emailInvalid': 'Skriv en gyldig e-mailadresse.',

  // ---------------------------------------------------------------------------
  // Reports
  // ---------------------------------------------------------------------------
  'reports.title': 'Rapporter',
  'reports.choose': 'Vælg rapport',
  'reports.tab.profitAndLoss': 'Resultat',
  'reports.tab.balance': 'Balance',
  'reports.tab.trial': 'Saldobalance',
  'reports.asOf': 'Pr. dato',
  'reports.revenue': 'Omsætning',
  'reports.expenses': 'Omkostninger',
  'reports.loss': 'Underskud',
  'reports.profit': 'Overskud',
  'reports.period': 'Perioden',
  'reports.chartAdvisory': 'Grafen er vejledende. Beløbene i tabellerne er de nøjagtige.',
  'reports.vatNotRevenueTitle': 'Moms er ikke omsætning',
  'reports.vatNotRevenue': 'Salgsmoms er penge, virksomheden skylder SKAT, og indgår ikke i omsætningen. Den står på momssiden.',
  'reports.assets': 'Aktiver',
  'reports.liabilities': 'Passiver',
  'reports.equity': 'Egenkapital',
  'reports.resultApartTitle': 'Periodens resultat står for sig',
  'reports.resultApart': '{amount} er ikke lagt ind i egenkapitalen. Det sker først ved en årsafslutning, som dette system endnu ikke foretager — og at udelade resultatet helt ville vise en difference, der ikke er en difference.',
  'reports.noEntries': 'Ingen posteringer i perioden.',
  'reports.col.account': 'Konto',
  'reports.col.name': 'Navn',
  'reports.total': 'I alt',
  'reports.balance': 'Saldo',
  'reports.amount': 'Beløb',
  'reports.inBalanceTitle': 'Balancen stemmer',
  'reports.inBalance': 'Debet og kredit går op. Difference {difference}.',
  'reports.outOfBalanceTitle': 'Balancen stemmer ikke',
  'reports.outOfBalance': 'Difference {difference}. Databasen afviser en postering, der ikke balancerer, så dette er ikke en almindelig fejlpostering — det betyder, at regnskabet ikke stemmer med sig selv. Undersøg det, før tallene bruges.',

  // ---------------------------------------------------------------------------
  // VAT
  // ---------------------------------------------------------------------------
  'vat.title': 'Moms',
  'vat.openPeriod': 'Åbn periode',
  'vat.scopeTitle': 'Tallene, ikke angivelsen',
  'vat.scope': 'Denne side viser salgsmoms, købsmoms og momstilsvar for perioden. Den udfylder ikke en momsangivelse og fordeler ikke koderne på rubrik A, B og C — den officielle kodeliste beskriver rubrikkerne i fritekst, og et gæt her ville betyde en forkert angivelse til SKAT. Tallene herfra overføres manuelt.',
  'vat.none': 'Ingen momsperioder endnu. Åbn den første.',
  'vat.col.period': 'Periode',
  'vat.col.status': 'Status',
  'vat.netVat': 'Momstilsvar',
  'vat.status.closed': 'Angivet',
  'vat.status.open': 'Åben',
  'vat.notDetermined': 'Ikke opgjort',
  'vat.loadFailed': 'Kunne ikke hente momstallene',
  'vat.provisionalTitle': 'Perioden er åben — tallene er ikke endelige',
  'vat.provisional': 'Beløbene opgøres ud fra bogføringen, hver gang siden hentes, og ændrer sig, når der bogføres i perioden. Først når perioden lukkes, fastfryses de som det, der blev angivet.',
  'vat.outgoing': 'Salgsmoms',
  'vat.incoming': 'Købsmoms',
  'vat.netVatHint': 'Salgsmoms minus købsmoms. Positivt beløb skal betales til SKAT.',
  'vat.noFixedRateTitle': 'Koder uden fast sats',
  'vat.noFixedRate': 'Disse koder har ingen fast sats i den officielle liste, så den bogførte moms kan ikke efterregnes mod en sats. Den beror på den, der bogførte.',
  'vat.uncodedTitle': 'Linjer uden momskode',
  'vat.uncoded': {"one": "{count} linje i perioden har ingen momskode. Det er oftest korrekt — en bankoverførsel eller en lønudbetaling har ingen. Men et køb uden kode er moms, virksomheden har ret til og ikke har trukket fra, og kun et menneske kan se forskel.", "other": "{count} linjer i perioden har ingen momskode. Det er oftest korrekt — en bankoverførsel eller en lønudbetaling har ingen. Men et køb uden kode er moms, virksomheden har ret til og ikke har trukket fra, og kun et menneske kan se forskel."},
  'vat.byCode': 'Fordelt på koder',
  'vat.col.code': 'Kode',
  'vat.col.description': 'Betegnelse',
  'vat.col.rate': 'Sats',
  'vat.col.base': 'Grundlag',
  'vat.filedTitle': 'Perioden er angivet',
  'vat.filedOn': 'Tallene er fastfryst som det, der blev angivet den {date}. Perioden kan ikke genåbnes, og der kan ikke bogføres med en dato i den. Rettelser hører til i den følgende periode — sådan foretages en dansk momsrettelse i forvejen.',
  'vat.filed': 'Tallene er fastfryst som det, der blev angivet. Perioden kan ikke genåbnes, og der kan ikke bogføres med en dato i den. Rettelser hører til i den følgende periode — sådan foretages en dansk momsrettelse i forvejen.',
  'vat.ownerOnlyTitle': 'Kun en ejer kan angive perioden',
  'vat.ownerOnly': 'At angive momsen er virksomhedens erklæring til SKAT, ikke et bogholderisk skridt. Du kan bogføre, åbne perioder og læse tallene.',
  'vat.fileAndClose': 'Angiv og luk perioden',
  'vat.irreversibleTitle': 'Dette kan ikke fortrydes',
  'vat.irreversible': 'Når perioden lukkes, sker der tre ting på én gang:',
  'vat.irreversible.freeze': 'Tallene fastfryses på perioden som det, der blev angivet — momstilsvar {amount}.',
  'vat.irreversible.noReopen': 'Perioden kan aldrig genåbnes.',
  'vat.irreversible.noPosting': 'Der kan aldrig mere bogføres med en dato inde i perioden.',
  'vat.irreversible.enforced': 'Databasen håndhæver alle tre. Rettelser hører til i den følgende periode.',
  'vat.closeFailed': 'Perioden blev ikke angivet',
  'vat.fileAndCloseShort': 'Angiv og luk',
  'vat.closed': 'Perioden er angivet',
  'vat.closedDetail': 'Momstilsvar {amount} er fastfryst.',
  'vat.opened': 'Periode åbnet',
  'vat.openTitle': 'Åbn momsperiode',
  'vat.openHelp': 'Begge datoer er inklusive. Perioder må ikke overlappe hinanden — en postering hører til præcis én angivelse. Længden er dit valg: danske momsperioder er måned, kvartal eller halvår afhængigt af omsætningen.',
  'vat.startDate': 'Startdato',
  'vat.endDate': 'Slutdato',
  'vat.openFailed': 'Perioden blev ikke åbnet',
  'vat.open': 'Åbn',
  'vat.startRequired': 'Vælg en startdato.',
  'vat.endRequired': 'Vælg en slutdato.',

  // ---------------------------------------------------------------------------
  // Attachments
  // ---------------------------------------------------------------------------
  'att.title': 'Bilag',
  'att.filterLabel': 'Filtrér efter tilstand',
  'att.filter.all': 'Alle',
  'att.none': 'Ingen bilag her endnu.',
  'att.col.filename': 'Filnavn',
  'att.col.state': 'Tilstand',
  'att.col.size': 'Størrelse',
  'att.col.uploaded': 'Uploadet',
  'att.state.unlinked': 'Ikke tilknyttet',
  'att.state.draftLinked': 'Foreslået',
  'att.state.linked': 'Tilknyttet',
  'att.uploaded': 'Bilag uploadet',
  'att.chooseFiles': 'Vælg bilag',
  'att.dropHere': 'Træk bilag hertil, eller klik for at vælge',
  'att.dropHelp': 'PDF, JPEG, PNG, TIFF, HEIC, tekst, CSV eller XML. Højst 25 MB.',
  'att.takePhotoLabel': 'Tag billede af bilag',
  'att.takePhoto': 'Tag billede',
  'att.rejectedTitle': 'Filen blev ikke uploadet',
  'att.uploadFailed': 'Kunne ikke uploade bilaget',
  'att.open': 'Åbn',
  'att.link': 'Tilknyt',
  'att.delete': 'Slet',
  'att.openFailed': 'Kunne ikke åbne bilaget',
  'att.openFallbackTitle': 'Åbn bilaget',
  'att.openFallbackHelp': 'Linket gælder kun i denne fane.',
  'att.link.title': 'Tilknyt {filename}',
  'att.link.irreversibleTitle': 'Dette kan ikke fortrydes',
  'att.link.irreversible': 'Når et bilag dokumenterer en postering, er det en del af regnskabet og skal opbevares i fem år. Det kan hverken slettes eller frakobles bagefter. En fejl rettes med en modpostering.',
  'att.link.entry': 'Postering',
  'att.link.entryPlaceholder': 'Vælg postering',
  'att.link.entryRequired': 'Vælg den postering, bilaget dokumenterer.',
  'att.link.failed': 'Kunne ikke tilknytte bilaget',
  'att.link.linked': 'Bilag tilknyttet',
  'att.delete.title': 'Slet bilag',
  'att.delete.body': '"{filename}" dokumenterer ingen postering og kan slettes. Filen fjernes permanent.',
  'att.delete.failed': 'Bilaget blev ikke slettet',
  'att.delete.deleted': 'Bilag slettet',

  // ---------------------------------------------------------------------------
  // MCP page
  // ---------------------------------------------------------------------------
  'mcp.title': 'MCP',
  'mcp.intro': 'Sådan kobler du en AI til dit regnskab. Adgangen hører til dig, ikke til et enkelt firma.',
  'mcp.tab.general': 'Om MCP',
  'mcp.tab.setup': 'Opsætning',
  'mcp.whatTitle': 'Hvad er MCP?',
  'mcp.what1': 'MCP er en standard, der lader et AI-program bruge et system direkte i stedet for at bede dig om at klikke rundt i det. Kobler du din AI-klient til, kan du skrive «hvilke posteringer mangler bilag i september?» eller «bogfør den her kvittering på 1.250 kr.», og den gør det gennem de samme regler som skærmbillederne her.',
  'mcp.what2': 'Den taler med API\u2019et — ikke med databasen. Alt, hvad der gælder for dig, gælder også for den: din rolle i firmaet, momsperioder der er lukket, posteringer der ikke kan slettes, og revisionssporet.',
  'mcp.asymmetryTitle': 'Læsning og skrivning er ikke ens',
  'mcp.asymmetry1a': 'AI\u2019en må ',
  'mcp.asymmetry1b': 'læse på tværs af dine firmaer',
  'mcp.asymmetry1c': '. Men ',
  'mcp.asymmetry1d': 'alt, den ændrer, nævner firmaet udtrykkeligt',
  'mcp.asymmetry1e': ' — hver gang, i selve kaldet — og svaret fortæller, hvilket firma den har handlet i.',
  'mcp.asymmetry2': 'Grunden er, at en samtale med en AI bliver lang, og det, den husker fra starten, falder ud undervejs. Et menneske kan se firmanavnet øverst på skærmen hele tiden; det kan den ikke. En forkert læsning er et spildt svar. En forkert postering kan kun rettes med en modpostering, som revisionssporet gemmer ved siden af — i fem år.',
  'mcp.asymmetry3': 'Revisionssporet registrerer, hvilken bruger, hvilket værktøj, hvilken adgangsnøgle og hvilket firma der stod bag hver ændring.',
  'mcp.canTitle': 'Hvad den kan',
  'mcp.col.area': 'Område',
  'mcp.col.reads': 'Læser',
  'mcp.col.writes': 'Ændrer',
  'mcp.cannotTitle': 'Hvad den ikke kan',
  'mcp.cannot.delete': 'Slette eller rette en postering. Det kan ingen — heller ikke dig.',
  'mcp.cannot.otherCompany': 'Nå et firma, du ikke selv er med i. Nøglen er din, og den rækker præcis lige så langt som dig.',
  'mcp.cannot.role': 'Mere end din rolle tillader. Er du revisor i et firma, kan din AI læse dér og ikke bogføre.',
  'mcp.cannot.silent': 'Ændre noget uden at sige hvor. Firmaet står i kaldet og i svaret.',
  'mcp.group.bookkeeping': 'Bogføring',
  'mcp.group.bookkeeping.reads': 'kladde, posteringer, kontoplan, saldi',
  'mcp.group.bookkeeping.writes': 'opret postering, opret dimension',
  'mcp.group.attachments': 'Bilag',
  'mcp.group.attachments.reads': 'liste, søgning, manglende bilag, læs indhold',
  'mcp.group.attachments.writes': 'upload, tilknyt til postering, gem OCR-resultat',
  'mcp.group.bank': 'Bank',
  'mcp.group.bank.reads': 'konti, transaktioner, afstemning',
  'mcp.group.bank.writes': 'afstem transaktion',
  'mcp.group.invoices': 'Fakturaer',
  'mcp.group.invoices.reads': 'liste, detaljer, nummerrækkefølge, forsendelser',
  'mcp.group.invoices.writes': 'opret kladde, udsted, send e-faktura',
  'mcp.group.vat': 'Moms',
  'mcp.group.vat.reads': 'perioder, momsangivelse',
  'mcp.group.vat.writes': 'luk periode',
  'mcp.group.reports': 'Rapporter',
  'mcp.group.reports.reads': 'balance, resultat, saldobalance, SAF-T',
  'mcp.group.reports.writes': '—',
  'mcp.group.inbound': 'Indgående post',
  'mcp.group.inbound.reads': 'modtagne mails, afsendere',
  'mcp.group.inbound.writes': 'godkend afsender',
  'mcp.group.company': 'Firma',
  'mcp.group.company.reads': 'stamdata, overblik',
  'mcp.group.company.writes': 'ret stamdata',
  'mcp.setup.browser.title': 'Forbind med browser-login',
  'mcp.setup.browser.badge': 'Anbefalet',
  'mcp.setup.browser.body': 'Sæt adressen ind i din AI-klient og log ind. Klienten sender dig hertil, du vælger firma, og den får sin egen nøgle. Du skal ikke kopiere en nøgle nogen steder.',
  'mcp.setup.browser.step1': 'Kopiér adressen herunder.',
  'mcp.setup.browser.step2': 'I Claude: Indstillinger → Connectors → Tilføj brugerdefineret connector. Sæt adressen ind. Der er ikke noget felt til en nøgle — sådan skal det være.',
  'mcp.setup.browser.step3': 'Log ind, vælg hvilket firma AI’en læser i som udgangspunkt, og tryk Tillad.',
  'mcp.setup.browser.noKey': 'Bagefter står adgangen på Min konto under klientens navn, og det er også dér, du lukker den igen.',
  'mcp.setup.file.title': 'Eller sæt en nøgle ind i en fil',
  'mcp.setup.file.body': 'Claude Code og de fleste editorer læser en opsætningsfil og kan sende en nøgle. Claudes desktop- og web-app kan ikke: dér findes browser-login og ikke andet.',
  'mcp.setup.file.company': 'En nøgle fra Min konto peger ikke på et firma. Er du med i mere end ét, så sæt «X-Company-Id» ind ved siden af Authorization — ellers ved serveren ikke, hvilke bøger den skal læse i.',
  'consent.title': '{client} beder om adgang til dit regnskab',
  'consent.intro': 'Giver du lov, får programmet sin egen nøgle. Den er din: den rækker præcis lige så langt som dig, og ikke længere.',
  'consent.can.read': 'Den kan læse på tværs af de firmaer, du er med i.',
  'consent.can.write': 'Den kan kun ændre noget ved at nævne firmaet i selve kaldet — hver gang — og svaret fortæller, hvilket firma den handlede i.',
    'consent.scopesLabel': 'Hvad du giver adgang til',
  'consent.scopesHelp':
    'Fravælg det, du ikke vil give. Du kan ikke give mere, end der er bedt om.',
'consent.can.role': 'Den kan ikke mere, end din rolle tillader. Er du revisor i et firma, kan den læse dér og ikke bogføre.',
  'consent.can.audit': 'Alt, den ændrer, står i revisionssporet med dig, værktøjet og nøglen.',
  'consent.companyLabel': 'Firma, som læsninger sker i',
  'consent.companyHelp': 'Kun læsninger. Skal den bogføre noget, nævner den selv firmaet i kaldet.',
  'consent.returnsTo': 'Du bliver sendt tilbage til {host}.',
  'consent.allow': 'Tillad',
  'consent.deny': 'Afvis',
  'consent.failedTitle': 'Adgangen blev ikke givet',
  'consent.goneTitle': 'Forespørgslen findes ikke længere',
  'consent.goneBody': 'Den er udløbet eller allerede brugt. Start forfra i det program, der sendte dig hertil.',
  'consent.missingTitle': 'Der er ikke noget at godkende',
  'consent.missingBody': 'Siden blev åbnet uden en forespørgsel. Start i det program, du vil give adgang.',
  'consent.revokeHint': 'Du kan tilbagekalde adgangen når som helst på Min konto.',
  'mcp.setup.step1a': 'Nøglen laves på ',
  'mcp.setup.step1b': ', ikke her. Den hører til dig og ikke til et firma, så den bor sammen med resten af dine egne oplysninger.',
  'mcp.setup.onceOnly': 'Nøglen vises én gang. Kopiér den med det samme — den gemmes kun som en hash og kan ikke vises igen.',
  'mcp.setup.goToAccount': 'Gå til Min konto',
  'mcp.setup.overHttp': 'Serveren kører over HTTP, så klienten skal ikke starte et program på din maskine. Den skal bare kunne nå adressen.',
  'mcp.setup.step3a': 'De fleste klienter — Claude Desktop, Claude Code, Cursor — læser en fil i det her format. Udskift ',
  'mcp.setup.step3b': ' med nøglen fra Min konto.',
  'mcp.setup.copyConfig': 'Kopiér opsætning',
  'mcp.setup.step4': '4. Prøv den',
  'mcp.setup.try': 'Spørg din AI om noget, den kun kan vide herfra — «hvor mange posteringer mangler bilag?» er en god første prøve. Svaret begynder med, hvilket firma den har kigget i.',
  'mcp.setup.noExpiryTitle': 'Nøglen udløber ikke af sig selv',
  'mcp.setup.noExpiry': 'Den bliver ved med at virke, indtil du tilbagekalder den på Min konto. Mister du maskinen, den ligger på, er det dér, du lukker den — og kun den. Dine andre nøgler og din adgangskode er uberørte.',

  // ---------------------------------------------------------------------------
  // My account
  // ---------------------------------------------------------------------------
  'account.title': 'Min konto',
  'account.intro': 'Dine egne oplysninger. De er de samme, uanset hvilket firma du kigger på.',
  'account.you': 'Dig',
  'account.loadFailed': 'Profilen kunne ikke hentes',
  'account.name': 'Navn',
  'account.nameHelp': 'Vises i revisionssporet ved siden af det, du har ændret.',
  'account.email': 'E-mail',
  'account.emailHelp': 'Bruges til at logge ind. Kan ikke ændres her endnu.',
  'account.lastLogin': 'Sidst logget ind {when}.',
  'account.nameSaved': 'Navnet er gemt.',
  'account.nameFailed': 'Navnet blev ikke gemt',
  'account.password': 'Adgangskode',
  'account.currentPassword': 'Nuværende adgangskode',
  'account.newPassword': 'Ny adgangskode',
  'account.repeatPassword': 'Gentag ny adgangskode',
  'account.passwordHelp': 'Når adgangskoden skiftes, bliver du logget ud alle andre steder end her. Det er meningen: man skifter adgangskode, fordi man er bange for, at en anden har den.',
  'account.changePassword': 'Skift adgangskode',
  'account.passwordChanged': 'Adgangskoden er skiftet',
  'account.passwordChangedDetail': 'Du er logget ud alle andre steder.',
  'account.passwordFailed': 'Adgangskoden blev ikke skiftet',
  'account.currentPasswordRequired': 'Skriv din nuværende adgangskode.',
  'account.newPasswordTooShort': 'Mindst 8 tegn.',
  'account.passwordsDiffer': 'De to adgangskoder er ikke ens.',
  'account.twoFactor': 'To-faktor',
  'account.twoFactorOn': 'Slået til',
  'account.twoFactorOff': 'Slået fra',
  'account.twoFactorHelp': 'En kode fra en app på din telefon, ud over adgangskoden. Regnskabet kan ikke slettes eller rettes — en postering kan kun modposteres — så den, der kan logge ind, kan sætte varige spor.',
  'account.turnOn': 'Slå til',
  'account.turnOff': 'Slå fra',
  'account.turnOnTitle': 'Slå to-faktor til',
  'account.turnOffTitle': 'Slå to-faktor fra',
  'account.turnOffPrompt': 'Skriv din adgangskode for at bekræfte. Bagefter kan du logge ind med adgangskoden alene.',
  'account.turnOnPrompt': 'Skriv din adgangskode. Så får du en QR-kode, du scanner med din authenticator-app.',
  'account.continue': 'Fortsæt',
  'account.scanPrompt': 'Scan koden med din authenticator-app, og skriv så det sekscifrede tal, appen viser.',
  'account.qrAlt': 'QR-kode til authenticator-app',
  'account.cannotScan': 'Kan du ikke scanne?',
  'account.backupCodes': 'Reservekoder',
  'account.backupCodesHelp': 'Gem dem et sikkert sted. De er den eneste vej ind, hvis du mister telefonen — de vises kun nu.',
  'account.copyBackupCodes': 'Kopiér reservekoder',
  'account.codeFromApp': 'Kode fra appen',
  'account.notOnYet': 'To-faktor er ikke slået til, før koden er godkendt.',
  'account.twoFactorEnabled': 'To-faktor er slået til',
  'account.twoFactorEnabledDetail': 'Gem dine reservekoder et sikkert sted.',
  'account.twoFactorDisabled': 'To-faktor er slået fra.',
  'account.tokens': 'Adgangsnøgler til AI',
  'account.tokensHelp': 'En nøgle, din AI bruger til at nå dine regnskaber gennem MCP. Den hører til dig, ikke til et enkelt firma — hvad den må i et firma, afgøres af din rolle dér, hver gang den bruges. Lav én pr. maskine, så du kan tilbagekalde præcis den, du mister.',
  'account.tokenOnceTitle': 'Nøglen vises kun nu',
  'account.tokenOnce': 'Kopiér den ind i din AI-klient med det samme. Den gemmes kun som en hash, så den kan ikke vises igen — mister du den, laver du en ny.',
  'account.copyToken': 'Kopiér nøgle',
    'account.tokenScopes': 'Hvad nøglen må',
  'account.tokenScopesHelp':
    'De tre nederste kan ikke fortrydes, og derfor skal de vælges til. Alt andet kan rettes bagefter med en modpostering.',
  'scope.mcp:read': 'Læse regnskabet',
  'scope.mcp:write': 'Bogføre, afstemme og fakturere',
  'scope.accounts:write': 'Ændre kontoplanen',
  'scope.vat:close': 'Lukke en momsperiode',
  'scope.einvoice:send': 'Sende e-fakturaer',
  'scope.mcp:readHelp': 'Kan se alt, men ikke ændre noget.',
  'scope.mcp:writeHelp': 'Kan rettes bagefter med en modpostering.',
  'scope.accounts:writeHelp':
    'Kan ikke fortrydes: en konto kan ikke slettes, og nummer og type kan ikke ændres bagefter.',
  'scope.vat:closeHelp': 'Kan ikke åbnes igen. Rettelser hører til i næste periode.',
  'scope.einvoice:sendHelp': 'Sender et dokument til en rigtig modtager i dit CVR-navn.',
'account.tokenName': 'Navn på nøglen',
  'account.tokenNameHelp': 'Så du kan kende den fra de andre.',
  'account.createToken': 'Lav nøgle',
  'account.tokenNameRequired': 'Giv nøglen et navn.',
  'account.createTokenFailed': 'Nøglen blev ikke lavet',
  'account.revokeFailed': 'Nøglen blev ikke tilbagekaldt',
  'account.tokensFailed': 'Nøglerne kunne ikke hentes',
  'account.noTokens': 'Du har ingen nøgler endnu.',
  'account.col.status': 'Status',
  'account.col.lastUsed': 'Sidst brugt',
  'account.revoke': 'Tilbagekald',
  'account.token.revoked': 'tilbagekaldt',
  'account.token.expired': 'udløbet',
  'account.token.active': 'aktiv',
  'account.revokedStay': 'Tilbagekaldte nøgler bliver stående på listen. Revisionssporet registrerer, hvilken nøgle der har lavet hver ændring, og så skal man kunne slå den op bagefter.',
  'account.language': 'Sprog',
  'account.languageSelect': 'Vælg sprog',
  'account.languageHelp': 'Sproget følger dig, ikke firmaet — det gemmes på din bruger, så en anden browser åbner på det samme.',
  'account.languageScopeTitle': 'Bøgerne er danske i begge sprog',
  'account.languageScope': 'Sproget gælder skærmene: menuer, knapper, hjælpetekster og fejl. Kontoplanen, momskoderne, SAF-T og OIOUBL bliver stående på dansk — de er Erhvervsstyrelsens og SKATs egne ord, og en revisor, der spørger til 1010 Salg af varer og ydelser, mener præcis den tekst.',
  'account.languageSaved': 'Sproget er gemt.',
  'account.languageFailed': 'Sproget blev ikke gemt',
  'account.language.da': 'Dansk',
  'account.language.en': 'English',

  // ---------------------------------------------------------------------------
  // Entry and invoice forms
  // ---------------------------------------------------------------------------
  'entryForm.posted': 'Bogført',
  'entryForm.date': 'Dato',
  'entryForm.description': 'Posteringstekst',
  'entryForm.reference': 'Bilagsnr.',
  'entryForm.accountPlaceholder': 'Kontonummer',
  'entryForm.accountLine': 'Konto linje {n}',
  'entryForm.textLine': 'Tekst linje {n}',
  'entryForm.debitLine': 'Debet linje {n}',
  'entryForm.creditLine': 'Kredit linje {n}',
  'entryForm.removeLine': 'Fjern linje {n}',
  'entryForm.invalidAmount': 'Ugyldigt beløb',
  'entryForm.inBalance': 'I balance',
  'entryForm.difference': 'Difference {amount}',
  'entryForm.bothSides': 'En linje kan have enten debet eller kredit — ikke begge dele.',
  'entryForm.failed': 'Kunne ikke bogføre',
  'entryForm.post': 'Bogfør',
  'entryForm.irreversible': 'En bogføring kan ikke rettes eller slettes bagefter. Fejl rettes med en modpostering.',
  'draft.created': 'Kladde oprettet',
  'draft.title': 'Ny fakturakladde',
  'draft.customer': 'Kunde',
  'draft.customerPlaceholder': 'Vælg kunde',
  'draft.date': 'Fakturadato',
  'draft.dueDate': 'Forfaldsdato',
  'draft.col.description': 'Beskrivelse',
  'draft.col.quantity': 'Antal',
  'draft.col.unitPrice': 'Stykpris',
  'draft.col.vatCode': 'Momskode',
  'draft.accountPlaceholder': 'Omsætning',
  'draft.removeLine': 'Fjern linje',
  'draft.addLine': 'Tilføj linje',
  'draft.subtotal': 'Subtotal',
  'draft.vatNote': 'Moms beregnes af momskodens sats, når fakturaen udstedes. En kode uden fast sats afvises frem for at blive regnet som 0 %.',
  'draft.failed': 'Kladden blev ikke oprettet',
  'draft.save': 'Gem kladde',

  // ---------------------------------------------------------------------------
  // Inbound email
  // ---------------------------------------------------------------------------
  'inbound.title': 'Indgående post',
  'inbound.whatTitle': 'Hvad siden gør',
  'inbound.what': 'Videresend en faktura til firmaets adresse, og den lægger sig i bilagslisten. Den bogfører ingenting af sig selv: et modtaget dokument er ikke knyttet til nogen postering, før et menneske knytter det.',
  'inbound.address': 'Adressen',
  'inbound.addressFailed': 'Adressen kunne ikke hentes',
  'inbound.noAddress': 'Der er ingen adresse endnu.',
  'inbound.inactive': 'Inaktiv',
  'inbound.noDomainTitle': 'Der kan ikke modtages post endnu',
  'inbound.noDomainA': 'Adressens første del er sat til ',
  'inbound.noDomainB': ', men der er ikke sat noget modtagerdomæne op på serveren. Ingen mail kan nå frem, før det er på plads.',
  'inbound.newAddress': 'Ny adresse',
  'inbound.newAddressHelp': 'Den del der står før @. Skal være entydig på tværs af hele installationen — det er en rigtig postkasse.',
  'inbound.saveAddress': 'Gem adresse',
  'inbound.ownerOnly': 'Kun en ejer kan ændre adressen.',
  'inbound.addressSet': 'Adressen er sat',
  'inbound.addressNotSet': 'Adressen blev ikke sat',
  'inbound.localPartInvalid': 'Små bogstaver, tal, punktum, bindestreg og underscore. Skal begynde og slutte med et bogstav eller tal.',
  'inbound.senders': 'Godkendte afsendere',
  'inbound.sendersHelp': 'Post fra andre adresser afvises — og bliver registreret, så den ikke forsvinder i stilhed.',
  'inbound.sendersFailed': 'Listen kunne ikke hentes',
  'inbound.noSendersTitle': 'Ingen afsendere er godkendt',
  'inbound.noSenders': 'Alt hvad der sendes til adressen bliver afvist, indtil mindst én afsender er godkendt.',
  'inbound.col.email': 'E-mail',
  'inbound.col.note': 'Note',
  'inbound.actions': 'Handlinger',
  'inbound.removeSender': 'Fjern {email}',
  'inbound.approveSender': 'Godkend en afsender',
  'inbound.senderPlaceholder': 'faktura@leverandoer.dk',
  'inbound.notePlaceholder': 'Leverandørens navn',
  'inbound.approve': 'Godkend',
  'inbound.approved': 'Afsender godkendt',
  'inbound.approvedDetail': '{email}. Bed afsenderen sende dokumentet igen.',
  'inbound.approveFailed': 'Afsenderen blev ikke godkendt',
  'inbound.revoked': 'Godkendelsen er fjernet',
  'inbound.revokeFailed': 'Godkendelsen blev ikke fjernet',
  'inbound.emailInvalid': 'Skriv en e-mailadresse.',
  'inbound.log': 'Modtaget post',
  'inbound.filter.all': 'Alt',
  'inbound.filter.accepted': 'Modtaget',
  'inbound.filter.rejected': 'Afvist',
  'inbound.logFailed': 'Posten kunne ikke hentes',
  'inbound.logEmpty': 'Der er ikke modtaget noget.',
  'inbound.col.received': 'Modtaget',
  'inbound.col.from': 'Afsender',
  'inbound.col.subject': 'Emne',
  'inbound.col.files': 'Filer',
  'inbound.col.outcome': 'Resultat',
  'inbound.approveThis': 'Godkend {email}',
  'inbound.logNote': 'Alt der kommer ind bliver registreret — også det der afvises. En faktura fra en ikke-godkendt afsender ville ellers forsvinde sporløst, og mangle i regnskabet uden at noget sted siger, at den blev sendt.',
  'inbound.outcome.accepted': 'Modtaget',
  'inbound.outcome.senderNotAllowed': 'Afsender ikke godkendt',
  'inbound.outcome.duplicate': 'Dublet',
  'inbound.outcome.unknownAddress': 'Ukendt adresse',
  'inbound.outcome.partial': 'Delvist modtaget',
  'inbound.outcome.noAttachments': 'Ingen vedhæftede filer',

  // ---------------------------------------------------------------------------
  // Inbound email
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Bank
  // ---------------------------------------------------------------------------
  'bank.title': 'Bank',
  'bank.newAccount': 'Ny bankkonto',
  'bank.noAccountsTitle': 'Ingen bankkonti endnu',
  'bank.noAccountsA': 'En bankkonto knyttes til præcis én konto i kontoplanen. Standardkontoplanen har 6480 Bankkonto.',
  'bank.noAccountsB1': 'Skal du bruge flere, er numrene 6481–6489 ledige — men kontoen skal ',
  'bank.noAccountsB2': 'oprettes i kontoplanen først',
  'bank.noAccountsB3': '. Den lægger sig så ind under 6490 Likvide beholdninger af sig selv.',
  'bank.account': 'Bankkonto',
  'bank.filterLabel': 'Filtrér efter afstemning',
  'bank.filter.all': 'Alle',
  'bank.filter.partial': 'Delvist',
  'bank.noTransactions': 'Ingen posteringer på kontoudtoget endnu.',
  'bank.col.date': 'Dato',
  'bank.col.text': 'Tekst',
  'bank.col.amount': 'Beløb',
  'bank.col.balance': 'Saldo',
  'bank.col.reconciliation': 'Afstemning',
  'bank.reconcile': 'Afstem',
  'bank.status.unmatched': 'Ikke afstemt',
  'bank.status.partial': 'Delvist afstemt',
  'bank.status.matched': 'Afstemt',
  'bank.summaryFailed': 'Kunne ikke hente afstemningen',
  'bank.unmatched': 'Ikke afstemt',
  'bank.transactionCount': 'Antal posteringer',
  'bank.lastStatement': 'Sidste kontoudtog',
  'bank.import': 'Importér kontoudtog',
  'bank.importHelp': 'Send filen præcis som banken eksporterede den. Åbnes den i Excel og gemmes, ændres tegnsæt eller separator, og filen er ikke længere dokumentation for hvad banken skrev.',
  'bank.file': 'Fil',
  'bank.filePlaceholder': 'Vælg CSV-fil',
  'bank.fileRequired': 'Vælg kontoudtogsfilen.',
  'bank.reader': 'Læser',
  'bank.readerHelp': 'Vælges automatisk ud fra filen',
  'bank.readerPlaceholder': 'Automatisk',
  'bank.importButton': 'Importér',
  'bank.alreadyImported': 'Allerede importeret',
  'bank.imported': 'Kontoudtog importeret',
  'bank.alreadyImportedDetail': 'Filen er identisk med en tidligere import. Intet blev tilføjet.',
  'bank.importedDetail': '{count} posteringer tilføjet.',
  'bank.importFailed': 'Kontoudtoget blev ikke importeret',
  'bank.readTitle': 'Kontoudtog læst',
  'bank.readSummary': '{reader} · {from}–{to} · {rows} rækker læst, {imported} tilføjet, {duplicates} allerede bogført.',
  'bank.gapTitle': 'Der mangler et kontoudtog imellem',
  'bank.gap': 'Forrige kontoudtog sluttede på {previous}, og dette begynder på {opening}. Importen er gennemført — huller er lovlige — men et kontoudtog mangler.',
  'bank.skippedTitle': 'Rækker sprunget over',
  'bank.accountCreated': 'Bankkonto oprettet',
  'bank.newAccountTitle': 'Ny bankkonto',
  'bank.accountName': 'Navn',
  'bank.accountNamePlaceholder': 'Nordea Erhverv',
  'bank.ledgerAccount': 'Konto i kontoplanen',
  'bank.ledgerMode': 'Hvordan kontoen i kontoplanen vælges',
  'bank.ledgerMode.existing': 'Vælg en konto',
  'bank.ledgerMode.new': 'Opret en ny konto',
  'bank.ledgerAccountPlaceholder': 'Søg efter nummer eller navn',
  'bank.ledgerAccountHelp':
    'Kan ikke ændres bagefter: posteringer afstemmes mod den. Kun aktivkonti, som ingen anden bankkonto allerede bruger.',
  'bank.noEligibleAccounts': 'Ingen ledige aktivkonti. Opret en ny konto i stedet.',
  'bank.newAccountCode': 'Kontonummer',
  'bank.newAccountCodeHelp':
    '6481–6489 er ledige i standardkontoplanen og lægger sig ind under 6490 Likvide beholdninger af sig selv. Kontoen oprettes som aktivkonto sammen med bankkontoen.',
  'bank.newAccountName': 'Kontonavn',
  'bank.newAccountNamePlaceholder': 'Bankkonto – Coffee Shop',
  'bank.newAccountCodeRequired': 'Kontonummeret skal udfyldes.',
  'bank.newAccountCodeDigits': 'Et kontonummer er kun cifre.',
  'bank.newAccountNameRequired': 'Kontoen skal have et navn.',
  'bank.iban': 'IBAN',
  'bank.ibanPlaceholder': 'Valgfri',
  'bank.accountFailed': 'Bankkontoen blev ikke oprettet',
  'bank.nameRequired': 'Bankkontoen skal have et navn.',
  'bank.ledgerAccountRequired': 'Vælg kontoen i kontoplanen.',

  // ---------------------------------------------------------------------------
  // Bank matching
  // ---------------------------------------------------------------------------
  'match.title': 'Afstem {date} · {amount}',
  'match.noBankText': 'Ingen tekst fra banken',
  'match.postedSoFar': 'Bogført indtil videre',
  'match.difference': 'Difference',
  'match.postedEntries': 'Bogførte posteringer',
  'match.removeMatch': 'Fjern match',
  'match.removeNote': 'Fjerner kun sammenkædningen. Posteringen bliver stående — posteringer er uforanderlige, så en fejlagtig postering rettes med en modpostering.',
  'match.removeFailed': 'Matchet blev ikke fjernet',
  'match.reconcile': 'Afstem',
  'match.modeLabel': 'Hvordan skal posteringen afstemmes',
  'match.mode.existing': 'Allerede bogført',
  'match.mode.fresh': 'Bogfør nu',
  'match.close': 'Luk',
  'match.existingHelp': 'En kundes betaling af en faktura, eller en leverandørfaktura der blev bogført før betalingen. Posteringen findes — den skal kun kædes sammen med banklinjen.',
  'match.entry': 'Postering',
  'match.entryPlaceholder': 'Vælg postering',
  'match.entryRequired': 'Vælg posteringen, banklinjen hører til.',
  'match.reconcileFailed': 'Kunne ikke afstemme',
  'match.link': 'Kæd sammen',
  'match.reconciled': 'Afstemt',
  'match.fullyReconciled': 'Banklinjen er fuldt afstemt.',
  'match.stillMissing': 'Der mangler stadig {amount}.',
  'match.freshWarningTitle': 'Bogfør kun hvis hændelsen ikke er bogført',
  'match.freshWarning': 'Bankkontoen tager automatisk den ene side. Er beløbet en kundes betaling af en faktura, der allerede er bogført, skal du bruge “Allerede bogført” i stedet — ellers indtægtsføres salget to gange, og der betales moms af det to gange.',
  'match.contraAccount': 'Modkonto',
  'match.contraAccountPlaceholder': 'Vælg konto',
  'match.contraAccountRequired': 'Vælg modkontoen.',
  'match.text': 'Tekst',
  'match.textRequired': 'Skriv en tekst til posteringen.',
  'match.entryDate': 'Posteringsdato',
  'match.entryDateRequired': 'Vælg posteringsdatoen.',
  'match.postFailed': 'Kunne ikke bogføre',
  'match.postAndReconcile': 'Bogfør og afstem',
  'match.posted': 'Bogført og afstemt',

  // ---------------------------------------------------------------------------
  // Invoice detail
  // ---------------------------------------------------------------------------
  'invDetail.draftTitle': 'Fakturakladde',
  'invDetail.title': 'Faktura {number}',
  'invDetail.noCustomer': 'Ingen kunde valgt',
  'invDetail.dates': '{date} · forfalder {dueDate}',
  'invDetail.total': 'I alt',
  'invDetail.subtotal': 'Subtotal {amount}',
  'invDetail.vat': 'Moms {amount}',
  'invDetail.close': 'Luk',
  'invDetail.draft': 'Kladde',
  'invDetail.deleteDraft': 'Slet kladde',
  'invDetail.draftDeleted': 'Kladde slettet',
  'invDetail.draftDeletedDetail': 'Ingen nummerrække blev berørt.',
  'invDetail.deleteFailed': 'Kladden blev ikke slettet',
  'invDetail.issue': 'Udsted faktura',
  'invDetail.irreversibleTitle': 'Dette kan ikke fortrydes',
  'invDetail.irreversible1': 'Udstedelse tildeler det næste fakturanummer og bogfører fakturaen i regnskabet — begge dele i én transaktion.',
  'invDetail.irreversible2': 'Derefter er fakturaen låst. Den kan hverken rettes eller slettes, fordi nummeret skal blive i rækken. En fejl rettes med en kreditnota.',
  'invDetail.missingAccountTitle': 'En linje mangler konto',
  'invDetail.missingAccount': 'Hver linje skal have en omsætningskonto, før fakturaen kan udstedes.',
  'invDetail.issueFailed': 'Fakturaen blev ikke udstedt',
  'invDetail.undo': 'Fortryd',
  'invDetail.issueAndPost': 'Udsted og bogfør',
  'invDetail.issued': 'Faktura {number} udstedt',
  'invDetail.issuedDetail': 'Nummeret er tildelt og fakturaen er bogført.',
  'invDetail.issuedSection': 'Udstedt',
  'invDetail.lockedTitle': 'Fakturaen er låst',
  'invDetail.locked': 'En udstedt faktura kan hverken rettes eller slettes — nummeret skal blive i rækken. En fejl rettes med en kreditnota, som trækker fra den samme nummerrække.',
  'invDetail.markPaid': 'Markér som betalt',
  'invDetail.markedPaid': 'Markeret som betalt',
  'invDetail.eInvoice': 'Elektronisk faktura',
  'invDetail.format': 'Format',
  'invDetail.format.oioubl': 'OIOUBL 2.1 (dansk)',
  'invDetail.format.peppol': 'Peppol BIS 3.0 (udenlandsk)',
  'invDetail.showDocument': 'Vis dokument',
  'invDetail.documentBuilt': 'Dokument bygget',
  'invDetail.documentBuiltDetail': 'Intet er sendt. Bygning er skrivebeskyttet.',
  'invDetail.send': 'Send',
  'invDetail.buildNote': 'Bygning er skrivebeskyttet og kan gentages, så dokumentet kan efterses, før noget når frem til en kunde. Dokumenterne valideres ikke mod UBL-skemaerne eller Peppols Schematron — en fejl opdages først af modtagerens access point.',
  'invDetail.documentFailed': 'Dokumentet kunne ikke bygges',
  'invDetail.sendFailed': 'Fakturaen blev ikke sendt',
  'invDetail.dispatches': 'Afsendelser',
  'invDetail.notDeliveredTitle': 'Ikke leveret',
  'invDetail.notDelivered1a': 'Fakturaen blev ',
  'invDetail.notDelivered1b': 'ikke',
  'invDetail.notDelivered1c': ' sendt til kunden. Der er ikke opsat noget access point, så afsendelsen kørte mod ',
  'invDetail.notDelivered1d': ', som bygger og registrerer dokumentet uden at åbne en forbindelse.',
  'invDetail.notDelivered2a': 'Afsendelsen er registreret som ',
  'invDetail.notDelivered2b': ', men det siger kun, at forsøget forløb uden fejl — ikke at noget nåede frem.',
  'invDetail.deliveredTitle': 'Leveret',
  'invDetail.delivered': '{number} sendt som {format} via {provider}.',
  'invDetail.receipt': ' Kvittering: {messageId}',

  // ---------------------------------------------------------------------------
  // Stragglers
  // ---------------------------------------------------------------------------
  'contacts.missingAddress': 'Mangler adresse — kan ikke e-faktureres',
  'contacts.edit': 'Rediger',
  'mcp.setup.keyPlaceholder': 'DIN_NØGLE_HER',
  'vat.undo': 'Fortryd',
  'vat.show': 'Vis',

  // ---------------------------------------------------------------------------
  // Audit trail
  // ---------------------------------------------------------------------------
  'audit.title': 'Revisionsspor',
  'audit.intro': 'Hvem har ændret hvad, hvornår — og med hvilken nøgle. Nyeste først.',
  'audit.forbiddenTitle': 'Din rolle kan ikke læse revisionssporet',
  'audit.forbidden': 'Revisionssporet er en optegnelse over, hvad dine kolleger har lavet. Det kan læses af en ejer, en revisor og en ekstern bogholder — de roller, hvis arbejde er at efterse. Du kan bogføre og læse regnskabet som hidtil.',
  'audit.filterAction': 'Handling',
  'audit.allActions': 'Alle handlinger',
  'audit.loadFailed': 'Revisionssporet kunne ikke hentes',
  'audit.empty': 'Der er ikke registreret noget, der passer.',
  'audit.col.when': 'Tidspunkt',
  'audit.col.action': 'Handling',
  'audit.col.who': 'Hvem',
  'audit.col.change': 'Ændring',
  'audit.showChange': 'Vis',
  'audit.hideChange': 'Skjul',
  'audit.newer': 'Nyere',
  'audit.older': 'Ældre',
  'audit.immutable': 'Revisionssporet kan hverken rettes eller slettes — databasen afviser det. Det opbevares i fem år sammen med regnskabet.',

  // ---------------------------------------------------------------------------
  // Kørebog
  // ---------------------------------------------------------------------------
  'nav.trips': 'Kørebog',
  'trip.title': 'Kørebog',
  'trip.intro': 'Erhvervskørsel i egen bil. Satsen er Skatterådets — den vælges ikke her, den slås op efter turens dato.',
  'trip.legalNote': 'Kørebogen er et lovkrav efter ligningsloven §9B. SKAT kan bede om den ved en kontrol, og den skal vise dato, start, slut, antal kilometer og formål for hver tur.',
  'trip.log': 'Log tur',
  'trip.logTitle': 'Log en tur',
  'trip.date': 'Dato',
  'trip.from': 'Startadresse',
  'trip.to': 'Slutadresse',
  'trip.km': 'Kilometer',
  'trip.purpose': 'Formål',
  'trip.purposeHelp': 'Fx "Kundemøde hos Sipotek A/S". SKAT spørger efter formålet.',
  'trip.isPrivate': 'Privat kørsel',
  'trip.isPrivateHelp': 'Privat kørsel føres også i kørebogen, men giver ingen godtgørelse og kan ikke bogføres.',
  'trip.useGps': 'Brug min position',
  'trip.gpsSearching': 'Finder position …',
  'trip.gpsDenied': 'Positionen blev afvist. Skriv adressen i stedet.',
  'trip.gpsUnavailable': 'Positionen kunne ikke findes. Skriv adressen i stedet.',
  'trip.save': 'Gem tur',
  'trip.saved': 'Turen er logget',
  'trip.saveFailed': 'Turen kunne ikke gemmes',
  'trip.queued': 'Ingen forbindelse — turen er gemt og sendes, når der er net igen.',
  'trip.post': 'Bogfør',
  'trip.posted': 'Turen er bogført',
  'trip.postFailed': 'Turen kunne ikke bogføres',
  'trip.postConfirmTitle': 'Bogfør turen?',
  'trip.postConfirm': 'Der oprettes en postering på {amount}. Posteringen kan ikke rettes bagefter — en fejl rettes med en modpostering.',
  'trip.delete': 'Slet',
  'trip.deleted': 'Turen er slettet',
  'trip.deleteFailed': 'Turen kunne ikke slettes',
  'trip.deleteConfirmTitle': 'Slet turen?',
  'trip.deleteConfirm': 'Turen er ikke bogført og fjernes helt fra kørebogen.',
  'trip.empty': 'Ingen ture endnu.',
  'trip.loadFailed': 'Kørebogen kunne ikke hentes',
  'trip.col.date': 'Dato',
  'trip.col.route': 'Tur',
  'trip.col.km': 'Km',
  'trip.col.rate': 'Sats',
  'trip.col.amount': 'Godtgørelse',
  'trip.col.status': 'Status',
  'trip.statusPosted': 'Bogført',
  'trip.statusDraft': 'Ikke bogført',
  'trip.private': 'Privat',
  'trip.year': 'År',
  'trip.ytd': 'Erhvervskilometer i år',
  'trip.ytdOfThreshold': '{km} af {threshold} km til høj sats',
  'trip.thresholdPassed': 'Grænsen på {threshold} km er passeret. Kilometer herover afregnes til lav sats.',
  'trip.totalDeduction': 'Godtgørelse i alt',
  'trip.unposted': 'Ikke bogført',
  'trip.unpostedCount': { one: '1 tur mangler at blive bogført', other: '{count} ture mangler at blive bogført' },
  'trip.rates': 'Satser',
  'trip.rateTier1': 'Indtil {threshold} km',
  'trip.rateTier2': 'Over {threshold} km',
  'trip.rateSource': 'Kilde',
  'trip.export': 'Hent til SKAT',
  'trip.exportFailed': 'Kørebogen kunne ikke hentes',
  'trip.exportPdfMissing': 'PDF-udtrækket er ikke bygget endnu. CSV-filen indeholder de samme oplysninger.',
  'trip.splitAcrossTiers': '{tier1} km til høj sats, {tier2} km til lav',

  // ---------------------------------------------------------------------------
  // Kamera
  // ---------------------------------------------------------------------------
  'camera.title': 'Fotografér bilag',
  'camera.starting': 'Åbner kameraet …',
  'camera.viewfinder': 'Kameraets billede',
  'camera.take': 'Tag billede',
  'camera.retake': 'Tag om',
  'camera.use': 'Brug billedet',
  'camera.previewAlt': 'Det billede, du lige har taget',
  'camera.reviewHelp': 'Kan du læse beløb, dato og leverandør? Ellers tag det om — bilaget gemmes i fem år.',
  'camera.fallbackTitle': 'Bruger telefonens eget kamera',
  'camera.unsupported': 'Denne browser kan ikke vise et søgerbillede. Telefonens eget kamera bruges i stedet.',
  'camera.denied': 'Adgang til kameraet blev afvist. Du kan stadig bruge telefonens eget kamera.',
  'camera.unavailable': 'Kameraet kunne ikke åbnes. Telefonens eget kamera bruges i stedet.',
  'camera.openSystemCamera': 'Åbn kamera',

  // ---------------------------------------------------------------------------
  // Offline
  // ---------------------------------------------------------------------------
  'offline.queued': { one: '1 ting venter på at blive sendt', other: '{count} ting venter på at blive sendt' },
  'offline.queuedHelp': 'Det, du har taget eller logget uden forbindelse, ligger på telefonen og sendes automatisk, når der er net igen.',
  'offline.sendNow': 'Send nu',
  'offline.sending': 'Sender …',
  'offline.sent': { one: '1 ting er sendt', other: '{count} ting er sendt' },
  'offline.blocked': 'Kan ikke sende endnu: {reason}',
  'offline.stuck': 'Serveren afviste den: {reason}',
  'offline.retry': 'Prøv igen',
  'offline.discard': 'Kassér',
  'offline.discardConfirm': 'Kassér for altid',
  'offline.discardHelp': 'Den ligger kun på denne enhed og kan ikke hentes tilbage.',
  'offline.otherCompany': 'andet firma',
  'offline.itemAttachment': 'Bilag {name}',
  'offline.itemTrip': 'Tur {date}: {from} → {to}',
  'offline.updateAvailable': 'Der er en ny version',
  'offline.updateHelp': 'Genindlæs for at tage den i brug.',
  'offline.update': 'Genindlæs',

  // ---------------------------------------------------------------------------
  // Mobil-app
  // ---------------------------------------------------------------------------
  'mobile.title': 'Mobil-app',
  'mobile.intro': 'Det, der gøres med telefonen i hånden: fotografér et bilag, og log en tur. Installeret på telefonen er appen kun de to handlinger — ingen menu og ingen bogføring, kun det firma, der gemmes i.',
  'mobile.installTitle': 'Installér på telefonen',
  'mobile.installHelp': 'Appen åbner direkte på de to knapper og virker også uden forbindelse.',
  'mobile.install': 'Installér app',
  'mobile.installed': 'Appen er installeret på denne enhed.',
  'mobile.installManual': 'Åbn {url} i Chrome på telefonen, tryk på ⋮ øverst til højre, og vælg "Installér app".',
  'mobile.open': 'Åbn app-visningen',
  'mobile.actionsTitle': 'Handlinger',
  'mobile.companyHint': 'Bilag og ture gemmes i det valgte firma.',

  // ---------------------------------------------------------------------------
  // Notifikationer
  // ---------------------------------------------------------------------------
  'push.title': 'Notifikationer',
  'push.intro': 'Få besked på telefonen, når et bilag har ligget uden bilagsnummer i mere end en uge, eller når en tur i kørebogen ikke er bogført.',
  'push.enable': 'Slå notifikationer til',
  'push.disable': 'Slå fra på denne enhed',
  'push.enabled': 'Notifikationer er slået til på denne enhed',
  'push.unsupported': 'Denne browser understøtter ikke notifikationer.',
  'push.notConfigured': 'Notifikationer er ikke sat op på denne server.',
  'push.denied': 'Du har afvist notifikationer for dette websted. Det kan slås til igen i browserens indstillinger.',
  'push.failed': 'Notifikationer kunne ikke slås til',
  'push.devices': 'Tilmeldte enheder',
  'push.deviceSince': 'tilmeldt {date}',
  'push.deviceExpired': 'udløbet',
  'push.noDevices': 'Ingen enheder er tilmeldt.',
} as const satisfies Catalogue<string>;

/** Every key that exists. Derived from Danish, which is the source. */
export type MessageKey = keyof typeof da;
